<script setup lang="ts">
/**
 * 源站 .login-model-dialog：414 宽 / #1D2027 / pad 14.4 19.2。
 * 登录：账号+密码，或手机号+验证码；注册：账号+手机+密码+邀请码+18+。
 */
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import type { AuthSubmitMode } from '@/entities/session/auth-errors';
import { sendLoginSms } from '@/features/auth/auth-api';
import { bindGoogleIdentityButton } from '@/features/auth/google-identity';
import {
  isMxRegisterPhoneComplete,
  normalizeMxRegisterPhone,
} from '@/features/auth/oro-mx-auth-phone';
import { clientEnv } from '@/shared/config/env';
import { t } from '@/shared/i18n/messages';
import { trackRegisterSubmit } from '@/shared/tracking/meta-pixel';
import {
  inviteFieldLocked,
  inviteFieldValue,
  referralCodeForRegister,
} from '@/shared/referral/invite-link';
import { useSupportChat } from '@/features/support/use-support-chat';
import { useSessionStore } from '@/stores/session-store';
import OroMxRegisterGiftTip from '@/widgets/app-shell/OroMxRegisterGiftTip.vue';

const ASSETS = {
  google: '/skins/oro-mx-h5/auth/google.png',
  guest: '/skins/oro-mx-h5/auth/guest.webp',
  logo: '/skins/oro-mx-h5/auth/life-logo.png',
} as const;

const props = withDefaults(
  defineProps<{
    agentCode?: string;
    mode?: AuthSubmitMode;
    referralCode?: string;
  }>(),
  { agentCode: '', mode: 'login', referralCode: '' },
);

const emit = defineEmits<{
  close: [];
  success: [];
  toast: [message: string];
}>();

const sessionStore = useSessionStore();
const supportChat = useSupportChat();
const gisHost = ref<HTMLElement | null>(null);
const googleReady = ref(false);
const submitLock = ref(false);
const submitBusy = computed(() => submitLock.value || sessionStore.submitting);
const inviteLocked = computed(() => inviteFieldLocked(props.referralCode, props.agentCode));
const mode = ref<AuthSubmitMode>(inviteLocked.value ? 'register' : props.mode);
const showPassword = ref(false);
const adultConfirmed = ref(true);
const smsLogin = ref(false);
const sendingSms = ref(false);
const smsCountdown = ref(0);
const localError = ref('');
let smsTimer: ReturnType<typeof setInterval> | null = null;

const form = reactive({
  loginName: '',
  password: '',
  phone: '',
  code: '',
  referralCode: inviteFieldValue(props.referralCode, props.agentCode),
});

type FieldKey = 'loginName' | 'password' | 'phone' | 'code' | 'referralCode';
const errorField = ref<FieldKey | ''>('');

function fieldClass(key: FieldKey, value: string) {
  return {
    'is-active': value.length > 0,
    'is-error': errorField.value === key,
  };
}

function onWrapperClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (target.closest('button')) return;
  target.closest('.login-model__wrapper')?.querySelector('input')?.focus();
}

watch(
  () => [props.referralCode, props.agentCode] as const,
  () => {
    const next = inviteFieldValue(props.referralCode, props.agentCode);
    if (next) {
      form.referralCode = next;
      mode.value = 'register';
    }
  },
);

watch(
  () => props.mode,
  (value) => {
    if (!inviteLocked.value) mode.value = value;
  },
);

const isRegister = computed(() => mode.value === 'register');
const visibleError = computed(() => localError.value || sessionStore.lastError);
const passwordOk = computed(() => form.password.length >= 6 && form.password.length <= 16);
const phoneOk = computed(() => isMxRegisterPhoneComplete(form.phone));
const submitLabel = computed(() => {
  if (submitBusy.value) {
    return isRegister.value ? t('auth.register.submitting') : t('auth.login.submitting');
  }
  return isRegister.value ? t('oro.header.register') : t('oro.header.login');
});
const smsButtonLabel = computed(() =>
  smsCountdown.value > 0 ? `${smsCountdown.value}s` : t('oro.auth.sendCode'),
);

function toggleSmsLogin() {
  smsLogin.value = !smsLogin.value;
  errorField.value = '';
  localError.value = '';
  sessionStore.clearError();
}

function setMode(next: AuthSubmitMode) {
  mode.value = next;
  smsLogin.value = false;
  errorField.value = '';
  localError.value = '';
  sessionStore.clearError();
}

function clearSmsTimer() {
  if (smsTimer) {
    clearInterval(smsTimer);
    smsTimer = null;
  }
}

function startSmsCountdown() {
  smsCountdown.value = 60;
  clearSmsTimer();
  smsTimer = setInterval(() => {
    smsCountdown.value -= 1;
    if (smsCountdown.value <= 0) clearSmsTimer();
  }, 1000);
}

async function sendSmsCode() {
  localError.value = '';
  sessionStore.clearError();
  if (!phoneOk.value) {
    errorField.value = 'phone';
    localError.value = t('oro.auth.err.phone');
    return;
  }
  sendingSms.value = true;
  try {
    await sendLoginSms({
      phone: `+52${form.phone}`,
      siteId: clientEnv.siteId,
    });
    errorField.value = '';
    startSmsCountdown();
  } catch {
    localError.value = t('oro.auth.smsFail');
  } finally {
    sendingSms.value = false;
  }
}

function validateForm() {
  if (!isRegister.value && smsLogin.value) {
    if (!phoneOk.value) {
      errorField.value = 'phone';
      localError.value = t('oro.auth.err.phone');
      return false;
    }
    if (!form.code.trim()) {
      errorField.value = 'code';
      localError.value = t('oro.auth.err.code');
      return false;
    }
    errorField.value = '';
    localError.value = '';
    return true;
  }
  if (!form.loginName.trim()) {
    errorField.value = 'loginName';
    localError.value = t('oro.auth.err.account');
    return false;
  }
  if (isRegister.value && !phoneOk.value) {
    errorField.value = 'phone';
    localError.value = t('oro.auth.err.phone');
    return false;
  }
  if (isRegister.value && form.code.trim().length !== 6) {
    errorField.value = 'code';
    localError.value = form.code.trim()
      ? t('oro.auth.err.codeLength')
      : t('oro.auth.err.code');
    return false;
  }
  if (!passwordOk.value) {
    errorField.value = 'password';
    localError.value = t('oro.auth.err.password');
    return false;
  }
  if (isRegister.value && !adultConfirmed.value) {
    errorField.value = '';
    localError.value = t('oro.auth.err.adult');
    return false;
  }
  errorField.value = '';
  localError.value = '';
  return true;
}

function onPhoneInput(event: Event) {
  const target = event.target as HTMLInputElement;
  form.phone = normalizeMxRegisterPhone(target.value);
}

function onCodeInput(event: Event) {
  const target = event.target as HTMLInputElement;
  form.code = target.value.replace(/\D/g, '').slice(0, 6);
}

async function submit() {
  if (submitLock.value || sessionStore.submitting) return;
  submitLock.value = true;
  sessionStore.clearError();
  try {
    if (!validateForm()) return;
    const loginName = form.loginName.trim();
    if (isRegister.value) trackRegisterSubmit();
    const success = !isRegister.value && smsLogin.value
      ? await sessionStore.loginBySms({
          phone: `+52${form.phone}`,
          code: form.code.trim(),
          referralCode:
            referralCodeForRegister(form.referralCode, props.referralCode, props.agentCode) ||
            undefined,
        })
      : isRegister.value
      ? await sessionStore.register({
          loginName,
          password: form.password,
          phone: `+52${form.phone}`,
          smsCode: form.code.trim(),
          referralCode:
            referralCodeForRegister(form.referralCode, props.referralCode, props.agentCode) ||
            undefined,
          agentCode: props.agentCode.trim() || undefined,
        })
      : await sessionStore.login({
          loginName,
          password: form.password,
        });
    if (success) emit('success');
  } finally {
    submitLock.value = false;
  }
}

async function continueGuest() {
  if (submitLock.value || sessionStore.submitting) return;
  submitLock.value = true;
  try {
    const success = await sessionStore.loginAsGuest();
    if (success) emit('success');
  } finally {
    submitLock.value = false;
  }
}

function onForgot() {
  emit('toast', t('oro.auth.contactSupport'));
  emit('close');
}

async function handleGoogleCredential(idToken: string) {
  if (submitLock.value || sessionStore.submitting) return;
  submitLock.value = true;
  try {
    const success = await sessionStore.loginByGoogle({
      idToken,
      referralCode:
        referralCodeForRegister(form.referralCode, props.referralCode, props.agentCode) || undefined,
      agentCode: props.agentCode.trim() || undefined,
    });
    if (success) emit('success');
  } finally {
    submitLock.value = false;
  }
}

async function bindGoogleButton() {
  const host = gisHost.value;
  const clientId = clientEnv.googleClientId;
  if (!host || !clientId) {
    googleReady.value = false;
    return;
  }
  googleReady.value = await bindGoogleIdentityButton({
    clientId,
    host,
    onCredential: handleGoogleCredential,
  });
}

onMounted(() => {
  supportChat.preload();
  void bindGoogleButton();
});

onBeforeUnmount(() => {
  clearSmsTimer();
  gisHost.value?.replaceChildren();
});

function onGoogle() {
  if (googleReady.value) return;
  emit('toast', t('oro.auth.googleUnavailable'));
}

function onSupport() {
  void supportChat.open();
}
</script>

<template>
  <div class="login-model-dialog">
    <div class="login-model">
      <header class="login-model__header">
        <a class="login-model__logo" href="/" @click.prevent="emit('close')">
          <img :src="ASSETS.logo" alt="logo" />
        </a>
        <button class="login-model__close" type="button" aria-label="close" @click="emit('close')">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" aria-hidden="true">
            <path
              fill="currentColor"
              d="M764.288 214.592 512 466.88 259.712 214.592a31.936 31.936 0 0 0-45.12 45.12L466.752 512 214.528 764.224a31.936 31.936 0 1 0 45.12 45.184L512 557.184l252.288 252.288a31.936 31.936 0 0 0 45.12-45.12L557.12 512.064l252.288-252.352a31.936 31.936 0 1 0-45.12-45.184z"
            />
          </svg>
        </button>
      </header>

      <form class="login-model__form" @submit.prevent="submit">
        <div v-if="!smsLogin" class="login-model__field" :class="fieldClass('loginName', form.loginName)">
          <div class="login-model__control">
            <span class="login-model__label">{{ t('oro.auth.account') }}</span>
            <div class="login-model__wrapper" @click="onWrapperClick">
              <input
                v-model="form.loginName"
                class="login-model__input"
                type="text"
                autocomplete="username"
                :aria-label="t('oro.auth.account')"
              />
              <i class="login-model__field-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                  <path
                    fill="currentColor"
                    d="M512 512a192 192 0 1 0 0-384 192 192 0 0 0 0 384m0 64a256 256 0 1 1 0-512 256 256 0 0 1 0 512m320 320v-96a96 96 0 0 0-96-96H288a96 96 0 0 0-96 96v96a32 32 0 1 1-64 0v-96a160 160 0 0 1 160-160h448a160 160 0 0 1 160 160v96a32 32 0 1 1-64 0"
                  />
                </svg>
              </i>
            </div>
          </div>
        </div>

        <div
          v-if="isRegister || smsLogin"
          class="login-model__field has-prefix"
          :class="fieldClass('phone', form.phone)"
        >
          <div class="login-model__control">
            <span class="login-model__dial">+52</span>
            <span class="login-model__label">{{ t('oro.auth.phone') }}</span>
            <div class="login-model__wrapper" @click="onWrapperClick">
              <input
                :value="form.phone"
                class="login-model__input"
                type="tel"
                inputmode="numeric"
                maxlength="10"
                autocomplete="tel"
                :aria-label="t('oro.auth.phone')"
                @input="onPhoneInput"
              />
              <button
                class="login-model__send"
                type="button"
                :disabled="sendingSms || smsCountdown > 0"
                @click.stop="sendSmsCode"
              >
                {{ smsButtonLabel }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="isRegister || smsLogin" class="login-model__field" :class="fieldClass('code', form.code)">
          <div class="login-model__control">
            <span class="login-model__label">{{ t('oro.auth.smsCode') }}</span>
            <div class="login-model__wrapper" @click="onWrapperClick">
              <input
                :value="form.code"
                class="login-model__input"
                type="tel"
                inputmode="numeric"
                maxlength="6"
                autocomplete="one-time-code"
                :aria-label="t('oro.auth.smsCode')"
                @input="onCodeInput"
              />
            </div>
          </div>
        </div>

        <div v-if="!smsLogin" class="login-model__field" :class="fieldClass('password', form.password)">
          <div class="login-model__control">
            <span class="login-model__label">{{ t('oro.auth.password') }}</span>
            <div class="login-model__wrapper" @click="onWrapperClick">
              <input
                v-model="form.password"
                class="login-model__input"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                :aria-label="t('oro.auth.password')"
              />
              <button class="login-model__eye" type="button" @click="showPassword = !showPassword">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M876.8 156.8c0-9.6-3.2-16-9.6-22.4s-12.8-9.6-22.4-9.6-16 3.2-22.4 9.6L736 220.8c-64-32-137.6-51.2-224-60.8-160 16-288 73.6-377.6 176S0 496 0 512s48 73.6 134.4 176c22.4 25.6 44.8 48 73.6 67.2l-86.4 89.6c-6.4 6.4-9.6 12.8-9.6 22.4s3.2 16 9.6 22.4 12.8 9.6 22.4 9.6 16-3.2 22.4-9.6l704-710.4c3.2-6.4 6.4-12.8 6.4-22.4m-646.4 528Q115.2 579.2 76.8 512q43.2-72 153.6-172.8C304 272 400 230.4 512 224c64 3.2 124.8 19.2 176 44.8l-54.4 54.4C598.4 300.8 560 288 512 288c-64 0-115.2 22.4-160 64s-64 96-64 160c0 48 12.8 89.6 35.2 124.8L256 707.2c-9.6-6.4-19.2-16-25.6-22.4m140.8-96Q352 555.2 352 512c0-44.8 16-83.2 48-112s67.2-48 112-48c28.8 0 54.4 6.4 73.6 19.2zM889.599 336c-12.8-16-28.8-28.8-41.6-41.6l-48 48c73.6 67.2 124.8 124.8 150.4 169.6q-43.2 72-153.6 172.8c-73.6 67.2-172.8 108.8-284.8 115.2-51.2-3.2-99.2-12.8-140.8-28.8l-48 48c57.6 22.4 118.4 38.4 188.8 44.8 160-16 288-73.6 377.6-176S1024 528 1024 512s-48.001-73.6-134.401-176"
                  />
                  <path
                    fill="currentColor"
                    d="M511.998 672c-12.8 0-25.6-3.2-38.4-6.4l-51.2 51.2c28.8 12.8 57.6 19.2 89.6 19.2 64 0 115.2-22.4 160-64 41.6-41.6 64-96 64-160 0-32-6.4-64-19.2-89.6l-51.2 51.2c3.2 12.8 6.4 25.6 6.4 38.4 0 44.8-16 83.2-48 112s-67.2 48-112 48"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="isRegister"
          class="login-model__field"
          :class="[fieldClass('referralCode', form.referralCode), { 'is-locked': inviteLocked }]"
        >
          <div class="login-model__control">
            <span class="login-model__label">{{ t('oro.auth.promo') }}</span>
            <div class="login-model__wrapper" @click="onWrapperClick">
              <input
                v-model="form.referralCode"
                class="login-model__input"
                type="text"
                :readonly="inviteLocked"
                :aria-readonly="inviteLocked"
                :aria-label="t('oro.auth.promo')"
              />
            </div>
          </div>
        </div>

        <div v-if="!isRegister" class="login-model__links">
          <a
            v-if="!smsLogin"
            class="login-model__forgot"
            href="javascript:;"
            @click.prevent="onForgot"
          >{{ t('oro.auth.forgot') }}</a>
          <button class="login-model__forgot" type="button" @click="toggleSmsLogin">
            {{ smsLogin ? t('oro.auth.passwordLogin') : t('oro.auth.smsLogin') }}
          </button>
        </div>

        <label v-if="isRegister" class="login-model__terms">
          <input v-model="adultConfirmed" class="login-model__check" type="checkbox" />
          <span>
            {{ t('oro.auth.terms') }}
            <a class="login-model__terms-link" href="javascript:;">{{ t('oro.auth.termsLink') }}</a>
          </span>
        </label>

        <p v-if="visibleError" class="login-model__error">{{ visibleError }}</p>

        <div class="login-model__submit-wrap">
          <OroMxRegisterGiftTip v-if="isRegister" variant="submit" />
          <button
            class="login-model__submit"
            type="submit"
            :disabled="submitBusy"
            :aria-busy="submitBusy"
          >
            {{ submitLabel }}
          </button>
        </div>

        <p class="login-model__switch">
          <span>{{ isRegister ? t('oro.auth.hasAccount') : t('oro.auth.noAccount') }}</span>
          <button type="button" @click="setMode(isRegister ? 'login' : 'register')">
            {{ isRegister ? t('oro.header.login') : t('oro.header.register') }}
          </button>
        </p>

        <div class="login-model__third">
          <div class="login-model__divider"><span>{{ t('oro.auth.orLogin') }}</span></div>
          <div class="login-model__third-row">
            <button class="login-model__third-btn" type="button" title="Google" @click="onGoogle">
              <img :src="ASSETS.google" alt="Google" width="56" height="56" />
              <div ref="gisHost" class="login-model__gis" aria-hidden="true" />
            </button>
          </div>
        </div>

      </form>

      <div class="login-model__footer">
        <button class="login-model__footer-item" type="button" @click="onSupport">
          <i class="login-model__footer-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
              <path
                fill="currentColor"
                d="M512 512a192 192 0 1 0 0-384 192 192 0 0 0 0 384m0 64a256 256 0 1 1 0-512 256 256 0 0 1 0 512m320 320v-96a96 96 0 0 0-96-96H288a96 96 0 0 0-96 96v96a32 32 0 1 1-64 0v-96a160 160 0 0 1 160-160h448a160 160 0 0 1 160 160v96a32 32 0 1 1-64 0"
              />
            </svg>
          </i>
          {{ t('oro.auth.support') }}
        </button>
        <span class="login-model__footer-sep" />
        <button class="login-model__footer-item login-model__footer-item--action" type="button" @click="continueGuest">
          <img class="login-model__fast-icon" :src="ASSETS.guest" alt="" width="40" height="40" />
          {{ t('oro.auth.guest') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-model-dialog {
  overflow: visible;
  box-sizing: border-box;
  width: 18.4rem;
  max-width: 100%;
  padding: 0.64rem 0.8533rem 0.8533rem;
  background: #1d2027;
  border-radius: 0.2133rem;
  box-shadow:
    rgb(0 0 0 / 4%) 0 0.32rem 0.8533rem 0.1067rem,
    rgb(0 0 0 / 8%) 0 0.2133rem 0.5333rem;
}

.login-model {
  overflow: visible;
  box-sizing: border-box;
  width: 16.6933rem;
  max-width: 100%;
  padding: 0.4267rem 0.2133rem 0;
  color: #fff;
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', sans-serif;
}

.login-model__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 2.1333rem;
  margin-bottom: 1.0667rem;
}

.login-model__logo,
.login-model__logo img {
  display: block;
  width: 6.3022rem;
  height: 2.1333rem;
  object-fit: contain;
}

.login-model__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.92rem;
  height: 1.92rem;
  padding: 0;
  color: #757577;
  background: rgb(255 255 255 / 8%);
  border: 0;
  border-radius: 0.4267rem;
}

.login-model__close svg {
  width: 0.96rem;
  height: 0.96rem;
}

.login-model__field {
  width: 16.2667rem;
  margin-bottom: 1.0667rem;
}

.login-model__control {
  position: relative;
  width: 100%;
  min-height: 2.3467rem;
}

.login-model__label {
  position: absolute;
  top: 50%;
  right: 1.92rem;
  left: 0.64rem;
  z-index: 2;
  padding: 0 0.2133rem;
  overflow: hidden;
  color: #757577;
  font-size: 0.8533rem;
  line-height: 1.2;
  white-space: nowrap;
  text-overflow: ellipsis;
  pointer-events: none;
  background: transparent;
  transform: translateY(-50%);
  transition:
    top 0.2s,
    transform 0.2s,
    font-size 0.2s,
    color 0.2s,
    background-color 0.2s;
}

.login-model__field.is-active .login-model__label,
.login-model__field:focus-within .login-model__label {
  top: 0;
  width: fit-content;
  padding: 0 0.3733rem;
  color: #c6f542;
  font-size: 0.64rem;
  background: #1d2027;
  transform: translateY(-50%);
}

.login-model__field.is-error .login-model__label,
.login-model__field.is-error.is-active .login-model__label,
.login-model__field.is-error:focus-within .login-model__label {
  color: #ee1e40;
}

.login-model__wrapper {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  width: 100%;
  height: 2.3467rem;
  min-height: 2.3467rem;
  padding: 0 0.64rem;
  cursor: text;
  background: transparent;
  border-radius: 0.2667rem;
  box-shadow: rgb(117 117 119 / 35%) 0 0 0 0.0533rem inset;
  transition: box-shadow 0.2s;
}

.login-model__field:focus-within .login-model__wrapper {
  box-shadow: 0 0 0 0.0533rem #c6f542 inset;
}

.login-model__field.is-error .login-model__wrapper,
.login-model__field.is-error:focus-within .login-model__wrapper {
  box-shadow: 0 0 0 0.0533rem #ee1e40 inset;
}

.login-model__input {
  box-sizing: border-box;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  height: 1.0667rem;
  padding: 0;
  margin: 0;
  color: #fff;
  font-size: 0.8533rem;
  line-height: 1.0667rem;
  appearance: none;
  background: transparent;
  border: 0;
  outline: none;
}

.login-model__input::placeholder {
  color: transparent;
}

.login-model__input:read-only {
  cursor: default;
}

.login-model__field.is-locked .login-model__wrapper {
  cursor: default;
}

.login-model__field-icon,
.login-model__eye {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 0.96rem;
  height: 0.96rem;
  color: #757577;
}

.login-model__field-icon {
  margin-left: 0.2133rem;
  pointer-events: none;
}

.login-model__field-icon svg,
.login-model__eye svg {
  width: 0.96rem;
  height: 0.96rem;
}

.login-model__eye {
  width: 1.3867rem;
  padding: 0 0.2133rem;
  background: transparent;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .login-model__label,
  .login-model__wrapper {
    transition: none;
  }
}

.login-model__field.has-prefix .login-model__input {
  margin-left: 1.7rem;
}

.login-model__field.has-prefix .login-model__label {
  left: 2.55rem;
  right: 4.8rem;
}

.login-model__field.has-prefix.is-active .login-model__label,
.login-model__field.has-prefix:focus-within .login-model__label {
  left: 0.64rem;
  right: auto;
}

.login-model__dial {
  position: absolute;
  top: 50%;
  left: 0.64rem;
  z-index: 2;
  color: #fff;
  font-size: 0.7467rem;
  line-height: 1;
  transform: translateY(-50%);
}

.login-model__send {
  flex-shrink: 0;
  margin-left: 0.32rem;
  padding: 0;
  color: #c6f542;
  font-size: 0.64rem;
  line-height: 1.0667rem;
  background: transparent;
  border: 0;
}

.login-model__send:disabled {
  color: #757577;
}

.login-model__links {
  display: flex;
  justify-content: space-between;
  margin: 0.2133rem 0 0.8533rem;
}

.login-model__links .login-model__forgot {
  margin: 0;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.login-model__forgot {
  display: block;
  margin: 0.2133rem 0 0.8533rem;
  color: #757577;
  font-size: 0.7467rem;
  line-height: 1.12rem;
  text-decoration: none;
}

.login-model__terms {
  display: flex;
  gap: 0.32rem;
  align-items: flex-start;
  width: 16.2667rem;
  margin: 0 0 0.64rem;
  color: #757577;
  font-size: 0.64rem;
  line-height: 0.96rem;
}

.login-model__check {
  flex-shrink: 0;
  width: 0.8533rem;
  height: 0.8533rem;
  margin-top: 0.1067rem;
  accent-color: #409eff;
}

.login-model__terms-link {
  color: #fff;
  text-decoration: none;
}

.login-model__submit-wrap {
  position: relative;
  display: flex;
  overflow: visible;
  flex-direction: column;
  align-items: center;
  width: 16.2667rem;
  margin-top: 0.8rem;
}

.login-model__error {
  margin: 0 0 0.4267rem;
  color: #ee1e40;
  font-size: 0.64rem;
}

.login-model__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16.2667rem;
  height: 2.3467rem;
  margin-top: 0;
  padding: 0;
  color: #fff;
  font-size: 0.8533rem;
  font-weight: 700;
  background: #ee1e40;
  border: 0;
  border-radius: 0.4267rem;
}

.login-model__submit:disabled {
  pointer-events: none;
  cursor: wait;
  opacity: 1;
}

.login-model__switch {
  margin: 0.8533rem 0 0.4267rem;
  color: #757577;
  font-size: 0.7467rem;
  line-height: 1.12rem;
  text-align: center;
}

.login-model__switch button {
  padding: 0;
  color: #fff;
  font-size: 0.7467rem;
  font-weight: 700;
  background: transparent;
  border: 0;
}

.login-model__third {
  margin-top: 0;
}

.login-model__divider {
  display: flex;
  gap: 0.64rem;
  align-items: center;
  margin: 1.0667rem 0 0.8533rem;
  color: #fff;
  font-size: 0.64rem;
  line-height: 0.64rem;
}

.login-model__divider::before,
.login-model__divider::after {
  flex: 1;
  height: 0.0533rem;
  content: '';
  background: #ee1e40;
}

.login-model__third-row {
  display: flex;
  justify-content: center;
  margin-top: 0.8533rem;
}

.login-model__third-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.56rem;
  height: 2.56rem;
  padding: 0;
  overflow: hidden;
  background: rgb(255 255 255 / 6%);
  border: 0;
  border-radius: 50%;
}

.login-model__gis {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  opacity: 0.02;
}

.login-model__gis :deep(div),
.login-model__gis :deep(iframe) {
  width: 100% !important;
  height: 100% !important;
}

.login-model__third-btn img {
  width: 2.4889rem;
  height: 2.4889rem;
  object-fit: contain;
}

.login-model__footer {
  display: flex;
  gap: 0.64rem;
  align-items: center;
  justify-content: center;
  width: 16.2667rem;
  margin-top: 1.0667rem;
  padding-top: 0.8533rem;
  border-top: 1px solid rgb(117 117 119 / 20%);
}

.login-model__footer-item {
  display: flex;
  gap: 0.32rem;
  align-items: center;
  justify-content: center;
  width: 7.4667rem;
  height: 0.96rem;
  padding: 0;
  color: #757577;
  font-size: 0.5867rem;
  line-height: 0.88rem;
  background: transparent;
  border: 0;
}

.login-model__footer-sep {
  width: 0.0533rem;
  height: 1.4933rem;
  background: rgb(117 117 119 / 40%);
}

.login-model__footer-icon,
.login-model__footer-icon svg,
.login-model__fast-icon {
  display: block;
  width: 0.96rem;
  height: 0.96rem;
  object-fit: contain;
}
</style>
